import { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'
import { Well } from '@pushly/aqe/lib/components'
import React, { FC } from 'react'
import { ReactNode, useState } from 'react'
import { BetterSelect } from '../../better-select/better-select'
import { Button, Modal, Popover, Tooltip } from 'antd'
import { InfoCircleOutlined, RotateLeftOutlined } from '@ant-design/icons'
import { getClassNames } from '../../../_utils/classnames'
import { AppMessageUiTemplate } from '@pushly/models/lib/structs/app-messages/app-message-ui-template'
import PreviewSDK from '@pushly/cuttlefish'
import classNames from 'classnames'

type AppMessagePreviewProps = {
    template: AppMessageUiTemplate
    hideHeader?: boolean
    loading?: boolean | ReactNode
}

type PreviewDeviceType = 'phone' | 'tablet'
type PreviewOrientation = 'portrait' | 'landscape'
type SystemMode = 'light' | 'dark'

type PreviewPlatformSelectProps = {
    value: DeliveryChannel
    onChange: (channel: DeliveryChannel) => void
    options: { value: DeliveryChannel; label: string }[]
}

type PreviewSystemModeSelectProps = {
    value: 'light' | 'dark'
    onChange: (mode: 'light' | 'dark') => void
}

type PreviewDeviceSelectProps = {
    value: PreviewDeviceType
    onChange: (deviceType: PreviewDeviceType) => void
}

type PreviewOrientationSelectProps = {
    value: PreviewOrientation
    onChange: (orientation: PreviewOrientation) => void
}

const PreviewPlatformSelect: FC<PreviewPlatformSelectProps> = ({ value, onChange, options }) => {
    return (
        <BetterSelect
            className="platform-select"
            prefix="Platform"
            disableSearch={true}
            onChange={(val: DeliveryChannel) => {
                onChange(val)
            }}
            value={[value]}
            options={options}
            disabled={options.length === 1}
        />
    )
}

const PreviewSystemModeSelect: FC<PreviewSystemModeSelectProps> = ({ value, onChange }) => {
    return (
        <BetterSelect
            className="theme-select"
            prefix="Theme"
            disableSearch={true}
            defaultValue={['light']}
            value={[value]}
            onChange={(v: 'light' | 'dark') => {
                onChange(v)
            }}
            options={[
                {
                    value: 'light',
                    label: 'Light',
                },
                {
                    value: 'dark',
                    label: 'Dark',
                },
            ]}
        />
    )
}

const PreviewDeviceTypeSelect: FC<PreviewDeviceSelectProps> = ({ value, onChange }) => {
    return (
        <BetterSelect
            className="device-type-select"
            prefix="Device Type"
            disableSearch={true}
            defaultValue={['phone']}
            value={[value]}
            onChange={(v: PreviewDeviceType) => {
                onChange(v)
            }}
            options={[
                {
                    value: 'phone',
                    label: 'Phone',
                },
                {
                    value: 'tablet',
                    label: 'Tablet',
                },
            ]}
        />
    )
}

const PreviewOrientationSelect: FC<PreviewOrientationSelectProps> = ({ value, onChange }) => {
    return (
        <BetterSelect
            className="orientation-select"
            prefix="Orientation"
            disableSearch={true}
            defaultValue={['portrait']}
            value={[value]}
            onChange={(v: PreviewOrientation) => {
                onChange(v)
            }}
            options={[
                {
                    value: 'portrait',
                    label: 'Portrait',
                },
                {
                    value: 'landscape',
                    label: 'Landscape',
                },
            ]}
        />
    )
}

export const AppMessagePreview = ({ template, hideHeader, loading }: AppMessagePreviewProps) => {
    const previewTemplate = template

    const sortedChannels = [...previewTemplate.channels].sort()
    const defaultChannel = sortedChannels.length === 2 ? DeliveryChannel.NATIVE_IOS : sortedChannels[0]

    const [channel, setChannel] = useState(defaultChannel)
    const [theme, setTheme] = useState<SystemMode>('light')
    const [landscapeVisible, setLandscapeVisible] = useState(false)

    const [advancedPreviewDeviceType, setAdvancedPreviewDeviceType] = useState<PreviewDeviceType>('phone')
    const [advancedPreviewOrientation, setAdvancedPreviewOrientation] = useState<PreviewOrientation>('portrait')
    const [advancedPreviewChannel, setAdvancedPreviewChannel] = useState<DeliveryChannel>(channel)
    const [advancedPreviewSystemMode, setAdvancedPreviewSystemMode] = useState<SystemMode>(theme)

    const channelOptions = sortedChannels.map((option) => {
        return {
            value: option,
            label: option === DeliveryChannel.NATIVE_ANDROID ? 'Android' : 'Apple / iOS',
        }
    })

    return (
        <Well
            className={getClassNames('app-message-builder-preview', 'nested')}
            title={
                <span>
                    <Tooltip title="The platform preview is a best-guess at how this app message will look across the most common devices for the selected operating system.">
                        Preview
                        <InfoCircleOutlined className="info-icon" />
                    </Tooltip>
                </span>
            }
            hideHeader={hideHeader}
            hideFooter={true}
        >
            <div className="preview-select">
                <PreviewPlatformSelect
                    value={channel}
                    onChange={(val: DeliveryChannel) => {
                        setChannel(val)
                        setAdvancedPreviewChannel(val)
                    }}
                    options={channelOptions}
                />

                <PreviewSystemModeSelect
                    value={theme}
                    onChange={(v: 'light' | 'dark') => {
                        setTheme(v)
                        setAdvancedPreviewSystemMode(v)
                    }}
                />

                <Popover
                    content={
                        <div className="orientation-toggle-popover">
                            Preview your App Message in landscape orientation and tablet mode.
                        </div>
                    }
                    trigger={['hover']}
                    placement="topLeft"
                >
                    <Button
                        className="orientation-toggle"
                        icon={<RotateLeftOutlined />}
                        onClick={() => {
                            setAdvancedPreviewOrientation('landscape')
                            setLandscapeVisible(true)
                        }}
                    />
                </Popover>
            </div>

            <Well
                className={classNames('app-message-portrait-preview')}
                mode="ghost"
                hideHeader={true}
                hideFooter={true}
            >
                <PreviewSDK.AppMessagePreview
                    channel={channel}
                    template={previewTemplate}
                    systemMode={theme}
                    loading={loading}
                />
            </Well>

            <Modal
                className="iam-advanced-preview-modal"
                open={landscapeVisible}
                title="App Message Previews"
                onOk={() => setLandscapeVisible(false)}
                onCancel={() => {
                    setLandscapeVisible(false)
                    setAdvancedPreviewDeviceType('phone')
                    setAdvancedPreviewOrientation('portrait')
                }}
                footer={false}
                closeIcon={false}
                width={1060}
                centered={true}
                destroyOnClose={true}
            >
                <div className="iam-advanced-preview-container">
                    <div className="iam-advanced-preview-header">
                        <PreviewDeviceTypeSelect
                            value={advancedPreviewDeviceType}
                            onChange={(v: PreviewDeviceType) => {
                                setAdvancedPreviewDeviceType(v)
                            }}
                        />
                        <PreviewPlatformSelect
                            value={channel}
                            onChange={(val: DeliveryChannel) => {
                                setChannel(val)
                                setAdvancedPreviewChannel(val)
                            }}
                            options={channelOptions}
                        />
                        <PreviewOrientationSelect
                            value={advancedPreviewOrientation}
                            onChange={(v: PreviewOrientation) => {
                                setAdvancedPreviewOrientation(v)
                            }}
                        />
                        <PreviewSystemModeSelect
                            value={theme}
                            onChange={(v: 'light' | 'dark') => {
                                setTheme(v)
                                setAdvancedPreviewSystemMode(v)
                            }}
                        />
                    </div>
                    <div className="iam-advanced-preview-content">
                        <PreviewSDK.AppMessagePreview
                            template={previewTemplate}
                            channel={advancedPreviewChannel}
                            deviceType={advancedPreviewDeviceType}
                            systemMode={advancedPreviewSystemMode}
                            orientation={advancedPreviewOrientation}
                        />
                    </div>
                </div>
            </Modal>
        </Well>
    )
}
